<template>
  <div
    ref="filtersContainer"
    class="filter-container-stable flex flex-wrap gap-2 pb-4 pl-0.5"
    role="list"
    :aria-label="$t('filter.filters_aria_label')"
  >
    <div role="listitem">
      <SortingMenu :values="plpSortingValues" />
    </div>
    <div
      v-for="filter in displayedFilters"
      :key="filter.slug"
      class="filter-container-stable flex"
      role="listitem"
    >
      <BooleanFilter
        v-if="filter.type === 'boolean'"
        :id="filter.name"
        :filter="filter"
        :model-value="getAppliedBooleanValue(filter.slug)"
        :filter-values="getAvailableFilterValues(filter.slug)"
        @update:model-value="$emit('applyBooleanFilter', filter.slug, $event)"
      />

      <LazyRangeFilter
        v-else-if="filter.type === 'range'"
        :id="filter.name"
        :filter="filter"
        :min-value="filter.values[0].min"
        :max-value="filter.values[0].max"
        :model-value="getPriceRange(filter.values[0].min, filter.values[0].max)"
        :filter-values="getAvailableFilterValues(filter.slug)"
        @update:model-value="$emit('applyPriceFilter', $event)"
        @reset="$emit('resetPriceFilter')"
      />

      <ColorFilter
        v-else-if="filter.slug === 'colorRange'"
        :id="filter.name"
        :filter="filter"
        :model-value="
          getFilterValuesForAppliedAttributeValues(
            filter.slug,
            appliedAttributeValues[filter.slug],
          )
        "
        :filter-values="getAvailableFilterValues(filter.slug)"
        @apply="$emit('applyAttributeFilter', filter.slug, $event.values)"
        @reset="$emit('resetFilter', filter.slug)"
      />

      <SizeFilter
        v-else-if="filter.slug === 'size'"
        :id="filter.name"
        :filter="filter"
        :model-value="
          getFilterValuesForAppliedAttributeValues(
            filter.slug,
            appliedAttributeValues[filter.slug],
          )
        "
        :filter-values="getAvailableFilterValues(filter.slug)"
        @apply="$emit('applyAttributeFilter', filter.slug, $event.values)"
        @reset="$emit('resetFilter', filter.slug)"
      />
      <SizeCategoryFilter
        v-else-if="filter.slug === 'sizeCategorySize'"
        :id="filter.name"
        :filter="filter"
        :model-value="
          getFilterValuesForAppliedAttributeValues(
            filter.slug,
            appliedAttributeValues[filter.slug],
          )
        "
        :filter-values="getAvailableFilterValues(filter.slug)"
        :ungrouped-filter-values="filter.values"
        :size-category-sort-order-override="sizeCategorySortOrder"
        @apply="$emit('applyAttributeFilter', filter.slug, $event.values)"
        @reset="$emit('resetFilter', filter.slug)"
      />

      <AttributesFilter
        v-else
        :id="filter.name"
        :filter="filter"
        :filter-values="getAvailableFilterValues(filter.slug)"
        :model-value="
          getFilterValuesForAppliedAttributeValues(
            filter.slug,
            appliedAttributeValues[filter.slug],
          )
        "
        @apply="$emit('applyAttributeFilter', filter.slug, $event.values)"
        @reset="$emit('resetFilter', filter.slug)"
      />
    </div>
    <div v-if="showMoreFiltersButton" role="listitem">
      <Button
        ref="moreFiltersButton"
        class="filter-container-stable flex items-center border-0 py-0 text-base"
        type="muted"
        data-test-id="toggle-more-filters-button"
        @click="showAllFilters = !showAllFilters"
      >
        {{
          showAllFilters ? $t('filter.less_filters') : $t('filter.more_filters')
        }}
        <IconPlus class="size-4" />
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  CentAmount,
  ProductFilter,
  ProductSearchQuery,
  TransformedFilter,
} from '@scayle/storefront-nuxt'
import { getGroupedFilterableValues } from '@scayle/storefront-nuxt'
import AttributesFilter from '~/components/filters/type/AttributesFilter.vue'
import ColorFilter from '~/components/filters/type/ColorFilter.vue'
import BooleanFilter from '#layers/base/components/filters/type/BooleanFilter.vue'
import SizeFilter from '#layers/base/components/filters/type/SizeFilter.vue'
import SizeCategoryFilter from '~/components/filters/type/SizeCategoryFilter.vue'

import type { RangeTuple } from '~/components/ui/sliders/RangeSlider.vue'
import type { FilterItemWithValues } from '#layers/base/types/filter'

type Props = {
  filters: FilterItemWithValues[]
  appliedFilter: ProductSearchQuery
  appliedBooleanValues: Record<string, boolean>
  appliedAttributeValues: Record<string, number[]>
  plpSortingValues: SortValue[]
  sizeCategorySortOrder?: string | string[] | null
}

const props = defineProps<Props>()
const { t, te } = useI18n()

const translateFilterName = (filter: FilterItemWithValues) => {
  const key = `filter.${filter.slug}`
  return te(key) ? t(key) : filter.name
}

defineEmits<{
  resetPriceFilter: []
  applyPriceFilter: [[start: number, end: number]]
  applyBooleanFilter: [string, boolean]
  resetFilter: [string]
  applyAttributeFilter: [slug: string, value: number]
}>()

const { filters } = toRefs(props)

const showAllFilters = ref(false)
const filtersContainer = ref<HTMLElement | null>(null)
const moreFiltersButton = ref<HTMLElement | null>(null)
const showMoreFiltersButton = ref(false)
const maxVisibleFilters = ref(3)

const getFilterValuesForAppliedAttributeValues = (
  slug: string,
  values?: number[],
) => {
  const filter = availableFilterValues.value[slug]
  if (!filter || !values) {
    return []
  }
  return filter.filter((v: any) => values.includes(v.value))
}

const updateFiltersDisplay = () => {
  if (!filtersContainer.value) {
    return
  }

  const containerWidth = filtersContainer.value.clientWidth
  const buttonWidth = moreFiltersButton.value?.offsetWidth || 0
  const filterWidth = 170

  const availableSlots = Math.floor(
    (containerWidth - buttonWidth) / filterWidth,
  )

  maxVisibleFilters.value = availableSlots
  showMoreFiltersButton.value = allowedFilters.value.length > availableSlots
}

const displayedFilters = computed(() => {
  return showAllFilters.value
    ? allowedFilters.value
    : allowedFilters.value.slice(0, maxVisibleFilters.value)
})

onMounted(() => {
  updateFiltersDisplay()
  window.addEventListener('resize', updateFiltersDisplay)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateFiltersDisplay)
})

const supportedFilters = computed(() => {
  const result = [
    'prices',
    'sale',
    'isNew',
    ...filters.value
      .filter((f: ProductFilter) => f.type === 'attributes')
      .filter((f: ProductFilter) => f.slug !== 'color')
      .map((f: ProductFilter) => f.slug),
  ]
  // HINT: There are 2 size fiters existing with slugs 'sizeCategorySize' and 'size'
  // sometimes both of them are returned from backend.
  // Only one should be displayed and 'sizeCategorySize' should always take precedence
  if (result.includes('sizeCategorySize')) {
    const i = result.indexOf('size')
    if (i > -1) {
      result.splice(i, 1)
    }
  }
  return result
})

const filtersPresentationOrder: Record<string, number> = {
  isNew: 0,
  sale: 1,
  colorRange: 2,
  sizeCategorySize: 3,
  size: 4,
  prices: 5,
}

const getFilterPresentationPosition = (filterSlug: string) =>
  filtersPresentationOrder[filterSlug] ?? 100

const allowedFilters = computed(() => {
  return filters.value
    .filter((f: ProductFilter) => supportedFilters.value.includes(f.slug))
    .map((f) => ({ ...f, name: translateFilterName(f) }))
    .sort(
      (a: ProductFilter, b: ProductFilter) =>
        getFilterPresentationPosition(a.slug) -
        getFilterPresentationPosition(b.slug),
    )
})

const availableFilterValues = computed(() =>
  getGroupedFilterableValues(supportedFilters.value, allowedFilters.value),
)
const emptyFilterValues: TransformedFilter[] = []
const getAvailableFilterValues = (slug: string) =>
  availableFilterValues.value[slug] ?? emptyFilterValues

const getAppliedBooleanValue = (filterSlug: string) => {
  return props.appliedBooleanValues[filterSlug] ?? false
}

const getPriceRange = (
  currentMin: CentAmount,
  currentMax: CentAmount,
): RangeTuple => {
  const appliedMin = props.appliedFilter.minPrice
  const appliedMax = props.appliedFilter.maxPrice

  const min = Math.max(currentMin, appliedMin ?? currentMin, 0)
  const max = Math.min(currentMax, appliedMax ?? currentMax)

  return [min, max]
}
</script>
